export const constructionSessionItems = [
	{
		type: "SET",
		stimulus: {
			id: 40198,
			content: {
				instruction:
					"Watch the video and select the correct answer from the options provided.",
				passage: {
					title: "Construction Case: Structural Integrity Assessment",
					paragraphs: [
						{
							text: "A scenario is presented. You see a description of a construction site manager contacting a tele-construction service with concerns about visible structural cracks and misalignment in a newly constructed building following recent heavy rains.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "constructVid001",
						url: "vi1z4IevHV6RpXiEACapp4p1",
						type: "video",
						transcript:
							"Hello, thanks for taking my call. I'm overseeing a construction site for a new commercial building, and I've noticed several concerning issues. There are visible cracks in the foundation and some misalignment in the support beams, particularly near the lower levels. We recently experienced heavy rains, and I suspect that water damage and soil subsidence may be compromising the building’s structural integrity. I need your expert opinion on whether we should perform further diagnostic tests or proceed with immediate remedial measures. Could you advise on the next steps?",
					},
				],
			},
			metadata: {
				type: "READING",
				skill: "READING",
				title: "Reading Stimulus",
			},
		},
		items: [
			{
				id: 40199,
				type: "MCSS",
				content: {
					parentId: 40198,
					parentType: "SET",
					stem: "Based on the construction site manager's description of the issues, which diagnostic method would you recommend to assess the structural integrity?",
					choices: [
						{
							numFirstSelections: 30,
							id: "choice1",
							stem: "Ground penetrating radar (GPR)",
							text: "Ground penetrating radar (GPR)",
							isCorrect: true,
							explanation: `
An **Ground Penetrating Radar (GPR)** is the most appropriate diagnostic test for this construction scenario. **GPR is the standard non-destructive evaluation tool** used to assess subsurface conditions, particularly for detecting structural anomalies such as foundation cracks, soil voids, and water intrusion. Given the site manager’s concerns—**visible cracks, misalignment of structural elements, and potential water damage following heavy rains**—GPR is the best choice to quickly determine underlying issues without causing further disturbance. It is widely available, cost-effective, and provides **real-time imaging of subsurface conditions**, making it ideal for early detection of structural compromise.

While other inspection techniques exist, they are **not the preferred initial choice** in this context. **Infrared thermography** may reveal surface temperature differences but does not penetrate to detect subsurface defects. **Magnetic particle inspection** is limited to ferromagnetic materials and is less effective in a concrete or composite construction environment, while **X-ray imaging**—although detailed—is typically reserved for highly specialized cases where additional structural detail is required.

For **an initial, non-invasive assessment of structural integrity**, **GPR remains the most effective and efficient method.**

**Key Takeaways:**
- **GPR provides real-time, non-destructive subsurface imaging** essential for early detection of structural issues.
- **It is ideal for evaluating foundation conditions and detecting internal anomalies** without compromising the structure.
- **Other methods offer specific benefits but do not provide the comprehensive initial evaluation** needed for this scenario.

**References:**
- American Society for Nondestructive Testing (ASNT). (2022). *Nondestructive testing in construction: GPR applications*. Retrieved from [https://www.asnt.org/](https://www.asnt.org/)
- Daniels, D. J. (2004). *Ground penetrating radar* (2nd ed.). The Institution of Engineering and Technology. Retrieved from [https://www.theiet.org/](https://www.theiet.org/)
- Tibbetts, R. S. (2019). *Applications of GPR in structural assessments*. NDT.net. Retrieved from [https://www.ndt.net/](https://www.ndt.net/)
							`,
						},
						{
							id: "choice2",
							numFirstSelections: 22,
							stem: "Infrared thermography",
							text: "Infrared thermography",
							isCorrect: false,
						},
						{
							id: "choice3",
							stem: "Magnetic particle inspection",
							text: "Magnetic particle inspection",
							isCorrect: false,
						},
						{
							id: "choice4",
							stem: "X-ray imaging",
							text: "X-ray imaging",
							isCorrect: false,
						},
					],
					instruction: "Choose the best answer",
					highlights: null,
					media: null,
				},
				tags: [
					{
						id: 6479,
						type: "Skill",
						label: "Reading",
						breadcrumb: "Reading",
					},
				],
				metadata: {
					type: "MCSS",
					skill: "READING",
					title: "Multiple Choice Single Selection",
					topics: ["Structural Assessment"],
				},
				response: {
					maximumScore: 1,
					correctResponse: [
						{
							id: "choice1",
						},
					],
					numChoicesRequired: {
						max: 1,
						min: 1,
					},
				},
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 401998,
			content: {
				instruction:
					"Watch the video and write your answer in the text box provided.",
				passage: {
					title: "Construction Case: Structural Integrity Assessment",
					paragraphs: [
						{
							text: "A scenario is presented. You see a description of a construction site manager contacting a tele-construction service with concerns about visible structural cracks and misalignment in a newly constructed building following recent heavy rains.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "constructVid001",
						url: "vi1z4IevHV6RpXiEACapp4p1",
						type: "video",
						transcript:
							"Hello, thanks for taking my call. I'm overseeing a construction site for a new commercial building, and I've noticed several concerning issues. There are visible cracks in the foundation and some misalignment in the support beams, particularly near the lower levels. We recently experienced heavy rains, and I suspect that water damage and soil subsidence may be compromising the building’s structural integrity. I need your expert opinion on whether we should perform further diagnostic tests or proceed with immediate remedial measures. Could you advise on the next steps?",
					},
				],
			},
			metadata: {
				type: "INTEGRATED_WRITING_RESPONSE",
				skill: "WRITING",
				title: "Integrated Writing Response",
				topics: [],
			},
		},
		items: [
			{
				id: 406574,
				versionId: 1,
				content: {
					parentId: 401998,
					parentType: "SET",
					stem: "Based on the site manager's description, what is your preliminary assessment of the structural issues? Provide a detailed explanation supporting your conclusion, including the suspected cause, the mechanism of damage, and any potential differential diagnoses.",
					choices: [],
					instruction:
						"Watch the video and write your answer in the text box provided.",
					media: null,
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Writing",
						breadcrumb: "Writing",
					},
				],
				response: {
					maximumScore: 15,
					subskillsSchema: [
						{
							name: "Clarity and Organization",
							icon: "file-document-outline",
							description: "Structure your response clearly.",
							maxScore: 5,
						},
						{
							name: "Crisis Management",
							icon: "shield-check-outline",
							description: "Effectively address the crisis situation.",
							maxScore: 5,
						},
						{
							name: "Justification",
							icon: "check-circle-outline",
							description: "Provide rationale for your decisions.",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "INTEGRATED_WRITING_RESPONSE",
					skill: "WRITING",
					title: "Integrated Writing Response",
					topics: [],
				},
				score: null,
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 401999,
			content: {
				instruction:
					"Watch the video and circle the abnormality visible in the image.",
				passage: {
					title: "Construction Case: Structural Integrity Assessment",
					paragraphs: [
						{
							text: "A scenario is presented. You see a description of a construction site manager contacting a tele-construction service with concerns about visible structural cracks and misalignment in a newly constructed building following recent heavy rains.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "constructVid001",
						url: "vi1z4IevHV6RpXiEACapp4p1",
						type: "video",
						transcript:
							"Hello, thanks for taking my call. I'm overseeing a construction site for a new commercial building, and I've noticed several concerning issues. There are visible cracks in the foundation and some misalignment in the support beams, particularly near the lower levels. We recently experienced heavy rains, and I suspect that water damage and soil subsidence may be compromising the building’s structural integrity. I need your expert opinion on whether we should perform further diagnostic tests or proceed with immediate remedial measures. Could you advise on the next steps?",
					},
				],
			},
			metadata: {
				type: "INTEGRATED_MARKING_ANSWER_STIMULUS",
				skill: "READING",
				title: "Integrated Marking Answer Stimulus",
				topics: [],
			},
		},
		items: [
			{
				id: 406575,
				versionId: 1,
				content: {
					parentId: 401999,
					parentType: "SET",
					stem: "Using the provided image of the building foundation, identify and circle the area showing the most prominent structural anomaly.",
					choices: [],
					instruction:
						"Watch the video and circle the abnormality visible in the image.",
					media: [
						{
							id: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/demo/acme/construction_foundation.png",
							url: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/demo/acme/foundation.jpg",
							type: "image",
						},
					],
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Marking",
						breadcrumb: "Marking",
					},
				],
				response: {
					maximumScore: 10,
					subskillsSchema: [
						{
							name: "Accuracy",
							icon: "crosshairs-gps",
							maxScore: 5,
							description:
								"Accurately identify and mark the area of structural concern.",
						},
						{
							name: "Speed",
							icon: "timer-outline",
							maxScore: 5,
							description:
								"Efficiently analyze and annotate the image without sacrificing accuracy.",
						},
					],
				},
				metadata: {
					type: "MARKING_ANSWER",
					skill: "MARKING",
					title: "Marking Answer",
					topics: [],
				},
				score: null,
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 401999,
			content: {
				instruction:
					"Watch the video and circle the abnormality visible in the image.",
				passage: {
					title: "Construction Case: Structural Integrity Assessment",
					paragraphs: [
						{
							text: "A scenario is presented. You see a description of a construction site manager contacting a tele-construction service with concerns about visible structural cracks and misalignment in a newly constructed building following recent heavy rains.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "constructVid001",
						url: "vi1z4IevHV6RpXiEACapp4p1",
						type: "video",
						transcript:
							"Hello, thanks for taking my call. I'm overseeing a construction site for a new commercial building, and I've noticed several concerning issues. There are visible cracks in the foundation and some misalignment in the support beams, particularly near the lower levels. We recently experienced heavy rains, and I suspect that water damage and soil subsidence may be compromising the building’s structural integrity. I need your expert opinion on whether we should perform further diagnostic tests or proceed with immediate remedial measures. Could you advise on the next steps?",
					},
				],
			},
			metadata: {
				type: "LS_STIMULUS",
				skill: "SPEAKING",
				title: "Listen Speak Stimulus",
				topics: ["LS Summary"],
			},
		},
		items: [
			{
				id: 405773434,
				content: {
					parentId: 401999,
					parentType: "SET",
					stem: "After reviewing the site manager’s description and diagnostic findings, clearly explain your recommended remedial measures and safety considerations. Use clear, non-technical language to ensure stakeholder understanding.",
					choices: [],
					instruction: "Watch the video and record your answer.",
					media: null,
				},
				tags: [
					{
						id: 6481,
						type: "Skill",
						label: "Speaking",
						breadcrumb: "Speaking",
					},
				],
				response: {
					maximumScore: 30,
					maximumDuration: 40,
					subskillsSchema: [
						{
							maxScore: 5,
							name: "Communication & Interpersonal Skills",
							icon: "message-outline",
							description: "Demonstrate effective verbal communication.",
						},
						{
							maxScore: 5,
							name: "Responsibility",
							icon: "account-check-outline",
							description: "Show accountability for the incident.",
						},
						{
							maxScore: 5,
							name: "Clarity",
							icon: "microphone-outline",
							description: "Speak clearly and coherently.",
						},
						{
							maxScore: 5,
							name: "Professionalism",
							icon: "briefcase-outline",
							description: "Maintain a professional tone.",
						},
						{
							maxScore: 5,
							name: "Solution Orientation",
							icon: "arrow-right-circle-outline",
							description: "Present clear corrective actions.",
						},
						{
							maxScore: 5,
							name: "Empathy",
							icon: "heart-outline",
							description:
								"Convey understanding and empathy toward stakeholders.",
						},
					],
				},
				metadata: {
					type: "AUDIO_INPUT_EMOTION",
					skill: "SPEAKING",
					title: "Audio Input Item",
					topics: ["Construction Analysis"],
				},
				score: 0,
			},
		],
	},
];
